import type { MediaAsset } from 'shared/@types';

export const advisorVersion = 1;
export const advisorMaxFavorites = 5;
export const advisorMaxFreeTextLength = 2000;

export type AdvisorTrait =
  | 'size'
  | 'energy'
  | 'grooming_needs'
  | 'hair_shedding'
  | 'barking_level'
  | 'learning_ability'
  | 'child_friendly';

export type AdvisorPreference = {
  target: number;
  weight: number;
  mode: 'target' | 'maximum' | 'minimum';
  range?: [number, number];
};
export type AdvisorProfile = Partial<Record<AdvisorTrait, AdvisorPreference>>;
export type AdvisorTranslate = (key: string, values?: Record<string, string | number>) => string;
export type AdvisorAnswer = {
  questionId: string;
  value: string;
  question: string;
  answer: string;
  sizeRange?: [number, number];
};
export type AdvisorFavoriteStage = 'ask' | 'select' | 'done';
export type AdvisorQuestion = {
  id: string;
  text: string;
  explanation: string;
  options: { value: string; label: string }[];
};

export type AdvisorFeedbackReason =
  | 'too_active'
  | 'too_large'
  | 'too_much_grooming'
  | 'too_much_shedding'
  | 'too_vocal'
  | 'appearance'
  | 'other';
export type AdvisorFeedback = {
  breed_id: number;
  reaction: 'like' | 'dislike';
  reason?: AdvisorFeedbackReason;
};
export type AdvisorCard = {
  breed: { id: number; slug: string; name: string; avatar?: MediaAsset | null };
  score: number | null;
  is_favorite: boolean;
  summary: string | null;
  assessment: 'mismatch' | 'possible' | 'insufficient';
  positives: AdvisorTrait[];
  tradeoffs: (AdvisorTrait | 'child_safety')[];
  data_warnings: (
    | { code: 'registered' | 'limits' | 'uniform' }
    | { code: 'missing'; traits: AdvisorTrait[] }
    | { code: 'specialized_use'; use: 'hunting' | 'livestock_guarding' }
  )[];
  traits?: Partial<Record<AdvisorTrait, number>>;
  data_sources?: { url: string; checked_at: string }[];
};

export type AdvisorSharedResult = {
  uuid: string;
  version: number;
  language: string;
  country: string;
  created_at: string;
  expires_at: string;
  cards: AdvisorCard[];
};

export function getAdvisorMatchScore(score: AdvisorCard['score'] | undefined): number | null {
  if (typeof score !== 'number' || !Number.isFinite(score) || score < 0 || score > 1) return null;
  return Math.round(score * 100);
}

const nonLatinSummaryLetters = new RegExp('(?!\\p{Script=Latin})\\p{Letter}', 'u');

export function getAdvisorCardCopy(card: Omit<AdvisorCard, 'score'>, t: AdvisorTranslate) {
  const traitName = (trait: AdvisorTrait) => t(`match.traits.${trait}`);
  // Historical results may contain descriptions saved before server validation.
  const summary = card.summary?.trim();
  return {
    summary:
      summary && !nonLatinSummaryLetters.test(summary)
        ? summary
        : t(`match.summaries.${card.assessment}`),
    positives: card.positives.map((trait) => t('match.positive', { trait: traitName(trait) })),
    tradeoffs: card.tradeoffs.map((trait) =>
      trait === 'child_safety'
        ? t('match.child_safety')
        : t('match.tradeoff', { trait: traitName(trait) }),
    ),
    data_warnings: card.data_warnings.map((warning) =>
      warning.code === 'missing'
        ? t('match.warnings.missing', { traits: warning.traits.map(traitName).join(', ') })
        : warning.code === 'specialized_use'
          ? t(`match.warnings.specialized_use.${warning.use}`)
          : t(`match.warnings.${warning.code}`),
    ),
  };
}

const advisorTraits: AdvisorTrait[] = [
  'size',
  'energy',
  'grooming_needs',
  'hair_shedding',
  'barking_level',
  'learning_ability',
  'child_friendly',
];
const isAdvisorTrait = (value: unknown): value is AdvisorTrait =>
  typeof value === 'string' && advisorTraits.includes(value as AdvisorTrait);

export function isAdvisorCard(card: unknown): card is AdvisorCard {
  if (!card || typeof card !== 'object') return false;
  const value = card as Partial<AdvisorCard>;
  const breed = value.breed;
  return (
    !!breed &&
    Number.isSafeInteger(breed.id) &&
    breed.id > 0 &&
    typeof breed.name === 'string' &&
    typeof breed.slug === 'string' &&
    (value.score === null ||
      (typeof value.score === 'number' &&
        Number.isFinite(value.score) &&
        value.score >= 0 &&
        value.score <= 1)) &&
    typeof value.is_favorite === 'boolean' &&
    (value.summary === null || typeof value.summary === 'string') &&
    ['mismatch', 'possible', 'insufficient'].includes(value.assessment ?? '') &&
    Array.isArray(value.positives) &&
    value.positives.every(isAdvisorTrait) &&
    Array.isArray(value.tradeoffs) &&
    value.tradeoffs.every((trait) => trait === 'child_safety' || isAdvisorTrait(trait)) &&
    (value.data_sources === undefined ||
      (Array.isArray(value.data_sources) &&
        value.data_sources.every(
          (source) =>
            source &&
            typeof source.url === 'string' &&
            /^https:\/\/www\.(?:akc\.org\/dog-breeds|skk\.se\/hundraser)\/[a-z0-9-]+\/$/.test(
              source.url,
            ) &&
            typeof source.checked_at === 'string' &&
            /^\d{4}-\d{2}-\d{2}$/.test(source.checked_at),
        ))) &&
    Array.isArray(value.data_warnings) &&
    value.data_warnings.every(
      (warning) =>
        warning &&
        typeof warning === 'object' &&
        (['registered', 'limits', 'uniform'].includes(warning.code) ||
          (warning.code === 'specialized_use' &&
            ['hunting', 'livestock_guarding'].includes(warning.use)) ||
          (warning.code === 'missing' &&
            Array.isArray(warning.traits) &&
            warning.traits.every(isAdvisorTrait))),
    )
  );
}
export type AdvisorConflict = {
  trait: AdvisorTrait;
  answer: string;
  text: string;
  question: string;
  original_value: number | null;
  proposed_value: number;
  original_mode: AdvisorPreference['mode'];
  proposed_mode: AdvisorPreference['mode'];
};
export type AdvisorSession = {
  uuid: string;
  version: number;
  profile: AdvisorProfile;
  answers: { question: string; answer: string }[];
  favorite_breed_ids: number[];
  feedback: AdvisorFeedback[];
  shown_breed_ids: number[];
  recommendations: AdvisorCard[];
  favorites: AdvisorCard[];
  conflicts: AdvisorConflict[];
  interpretations: {
    trait: AdvisorTrait;
    from: number | null;
    to: number;
    source_quote: string;
  }[];
  free_text?: string | null;
  ai_status: 'ready' | 'unavailable' | 'skipped';
  description_revision?: string;
  descriptions_status?: 'pending' | 'ready' | 'unavailable' | 'skipped';
  has_more: boolean;
};
export type AdvisorResponse = { session: AdvisorSession; token?: string };
export type AdvisorRound = {
  id: string;
  descriptionRevision?: string;
  answerCount: number;
  kind: 'initial' | 'refine' | 'more' | 'conflict';
  cards: AdvisorCard[];
  favorites: AdvisorCard[];
  feedback?: AdvisorFeedback;
  text?: string;
  conflicts?: AdvisorConflict[];
  resolution?: { trait: AdvisorTrait; choice: 'answer' | 'text' };
};

const maximum = (target: number, weight = 4): AdvisorPreference => ({
  target,
  weight,
  mode: 'maximum',
});
const minimum = (target: number, weight = 3): AdvisorPreference => ({
  target,
  weight,
  mode: 'minimum',
});
const target = (value: number, weight = 3): AdvisorPreference => ({
  target: value,
  weight,
  mode: 'target',
});

// Missing preferences stay absent. In particular, experience and time alone do not
// become invented breed characteristics or stand in for a dog's individual needs.
const questionRules: {
  id: string;
  options: { value: string; profile?: AdvisorProfile }[];
}[] = [
  {
    id: 'activity',
    options: [
      { value: 'gentle', profile: { energy: maximum(2, 5) } },
      { value: 'regular', profile: { energy: maximum(3, 5) } },
      { value: 'active', profile: { energy: target(4, 4) } },
      { value: 'working', profile: { energy: minimum(4, 4) } },
    ],
  },
  {
    id: 'household',
    options: [
      { value: 'adults' },
      { value: 'children', profile: { child_friendly: minimum(4, 4) } },
      { value: 'other_pets' },
      { value: 'both', profile: { child_friendly: minimum(4, 4) } },
    ],
  },
  {
    id: 'size',
    options: [
      { value: 'small', profile: { size: maximum(2, 4) } },
      { value: 'medium', profile: { size: target(3, 3) } },
      { value: 'large', profile: { size: minimum(4, 3) } },
      { value: 'any' },
    ],
  },
  {
    id: 'grooming',
    options: [
      { value: 'simple', profile: { grooming_needs: maximum(2, 4) } },
      { value: 'regular', profile: { grooming_needs: maximum(3, 3) } },
      { value: 'involved' },
      { value: 'unsure' },
    ],
  },
  {
    id: 'training',
    options: [
      { value: 'everyday' },
      { value: 'keen', profile: { learning_ability: minimum(4, 3) } },
      { value: 'sport', profile: { learning_ability: minimum(4, 4) } },
      { value: 'unsure' },
    ],
  },
  {
    id: 'barking',
    options: [
      { value: 'sensitive', profile: { barking_level: maximum(2, 4) } },
      { value: 'moderate', profile: { barking_level: maximum(3, 2) } },
      { value: 'flexible' },
    ],
  },
  {
    id: 'shedding',
    options: [
      { value: 'little', profile: { hair_shedding: maximum(2, 3) } },
      { value: 'some', profile: { hair_shedding: maximum(3, 2) } },
      { value: 'flexible' },
    ],
  },
  {
    id: 'experience',
    options: [{ value: 'first' }, { value: 'shared' }, { value: 'own' }, { value: 'experienced' }],
  },
  {
    id: 'schedule',
    options: [{ value: 'home' }, { value: 'help' }, { value: 'planning' }, { value: 'unsure' }],
  },
];

export function createAdvisorQuestions(t: AdvisorTranslate): AdvisorQuestion[] {
  return questionRules.map(({ id, options }) => ({
    id,
    text: t(`questions.${id}.text`),
    explanation: t(`questions.${id}.explanation`),
    options: options.map(({ value }) => ({ value, label: t(`questions.${id}.options.${value}`) })),
  }));
}

export function isAdvisorSizeRange(value: unknown): value is [number, number] {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    value.every((bound) => Number.isInteger(bound) && bound >= 1 && bound <= 5) &&
    value[0] <= value[1]
  );
}

export function createAdvisorSizeAnswer(
  min: number,
  max: number,
  t: AdvisorTranslate,
): AdvisorAnswer | null {
  if (!isAdvisorSizeRange([min, max])) return null;
  const from = t(`questions.size.sizes.${min}`);
  const to = t(`questions.size.sizes.${max}`);
  return {
    questionId: 'size',
    value: 'range',
    sizeRange: [min, max],
    question: t('questions.size.text'),
    answer:
      min === 1 && max === 5
        ? t('questions.size.range_any')
        : min === max
          ? from
          : t('questions.size.range_answer', { from, to }),
  };
}

function isQuestionAnswer(question: (typeof questionRules)[number], answer: AdvisorAnswer) {
  if (question.id !== answer?.questionId) return false;
  if (answer.value === 'range') {
    return question.id === 'size' && isAdvisorSizeRange(answer.sizeRange);
  }
  return (
    answer.sizeRange === undefined && question.options.some(({ value }) => value === answer.value)
  );
}

export function buildAdvisorProfile(answers: AdvisorAnswer[]): AdvisorProfile {
  const profile: AdvisorProfile = {};
  for (const question of questionRules) {
    const answer = answers.find((entry) => entry.questionId === question.id);
    if (answer && question.id === 'size' && answer.value === 'range') {
      if (isAdvisorSizeRange(answer.sizeRange)) {
        const [min, max] = answer.sizeRange;
        if (min !== 1 || max !== 5) {
          profile.size = { target: (min + max) / 2, weight: 3, mode: 'target', range: [min, max] };
        }
      }
      continue;
    }
    const option = question.options.find((entry) => entry.value === answer?.value);
    if (option?.profile) {
      for (const [trait, preference] of Object.entries(option.profile)) {
        profile[trait as AdvisorTrait] = { ...preference };
      }
    }
  }
  return profile;
}

export function getAdvisorCoverage(answers: AdvisorAnswer[]) {
  const answered = questionRules.filter((question) =>
    answers.some((answer) => isQuestionAnswer(question, answer)),
  ).length;
  const coreAnswered = questionRules
    .slice(0, 5)
    .every((question) => answers.some((answer) => isQuestionAnswer(question, answer)));
  return {
    answered,
    total: questionRules.length,
    percent: Math.round((answered / questionRules.length) * 100),
    canMatch: coreAnswered && Object.keys(buildAdvisorProfile(answers)).length > 0,
  };
}

export function applyAdvisorFeedback(
  profile: AdvisorProfile,
  feedback: AdvisorFeedback,
  rejectedTraits?: Partial<Record<AdvisorTrait, number>>,
): AdvisorProfile {
  const traitByReason: Partial<Record<AdvisorFeedbackReason, AdvisorTrait>> = {
    too_active: 'energy',
    too_large: 'size',
    too_much_grooming: 'grooming_needs',
    too_much_shedding: 'hair_shedding',
    too_vocal: 'barking_level',
  };
  const trait = feedback.reason ? traitByReason[feedback.reason] : undefined;
  if (feedback.reaction !== 'dislike' || !trait) return { ...profile };
  const rejectedValue = rejectedTraits?.[trait];
  if (typeof rejectedValue !== 'number' || !Number.isFinite(rejectedValue)) return { ...profile };
  const previous = profile[trait];
  if (trait === 'size' && previous?.range) {
    const max = Math.max(1, Math.min(previous.range[1], Math.ceil(rejectedValue) - 1));
    const min = Math.min(previous.range[0], max);
    return {
      ...profile,
      size: {
        target: (min + max) / 2,
        weight: Math.max(previous.weight, 4),
        mode: 'target',
        range: [min, max],
      },
    };
  }
  return {
    ...profile,
    [trait]: maximum(
      Math.max(1, Math.min(previous?.target ?? 5, rejectedValue - 1)),
      Math.max(previous?.weight ?? 0, 4),
    ),
  };
}

export function advisorAnswerSnapshots(answers: AdvisorAnswer[]) {
  return answers.map(({ question, answer }) => ({ question, answer }));
}

export type AdvisorDraft = {
  version: 1;
  updatedAt: number;
  answers: AdvisorAnswer[];
  favoriteIds: number[];
  favoriteStage?: AdvisorFavoriteStage;
  favoriteInterest?: boolean;
  freeText: string;
  sessionId?: string;
  token?: string;
  rounds: AdvisorRound[];
  submittedAnswerCount: number;
};

export function parseAdvisorDraft(stored: string | null, now = Date.now()): AdvisorDraft | null {
  if (!stored || stored.length > 500_000) return null;
  try {
    const draft = JSON.parse(stored);
    if (
      draft?.version !== advisorVersion ||
      !Number.isFinite(draft.updatedAt) ||
      draft.updatedAt < 0 ||
      draft.updatedAt > now ||
      !Array.isArray(draft.answers) ||
      draft.answers.length > questionRules.length ||
      !draft.answers.every(
        (answer: AdvisorAnswer) =>
          questionRules.some((question) => isQuestionAnswer(question, answer)) &&
          typeof answer.question === 'string' &&
          answer.question.length <= 500 &&
          typeof answer.answer === 'string' &&
          answer.answer.length <= 500,
      ) ||
      new Set(draft.answers.map((answer: AdvisorAnswer) => answer.questionId)).size !==
        draft.answers.length ||
      !Array.isArray(draft.favoriteIds) ||
      draft.favoriteIds.length > advisorMaxFavorites ||
      !draft.favoriteIds.every((id: number) => Number.isSafeInteger(id) && id > 0) ||
      (draft.favoriteStage !== undefined &&
        !['ask', 'select', 'done'].includes(draft.favoriteStage)) ||
      (draft.favoriteInterest !== undefined && typeof draft.favoriteInterest !== 'boolean') ||
      typeof draft.freeText !== 'string' ||
      draft.freeText.length > advisorMaxFreeTextLength ||
      !Array.isArray(draft.rounds) ||
      draft.rounds.length > 100 ||
      !draft.rounds.every(
        (round: AdvisorRound) =>
          round &&
          typeof round.id === 'string' &&
          round.id.length <= 100 &&
          (round.descriptionRevision === undefined ||
            (typeof round.descriptionRevision === 'string' &&
              round.descriptionRevision.length <= 128)) &&
          Number.isInteger(round.answerCount) &&
          round.answerCount >= 0 &&
          round.answerCount <= draft.answers.length &&
          ['initial', 'refine', 'more', 'conflict'].includes(round.kind) &&
          (round.text === undefined ||
            (typeof round.text === 'string' && round.text.length <= advisorMaxFreeTextLength)) &&
          Array.isArray(round.cards) &&
          Array.isArray(round.favorites) &&
          [...round.cards, ...round.favorites].every(isAdvisorCard) &&
          (round.feedback === undefined ||
            (Number.isSafeInteger(round.feedback?.breed_id) &&
              round.feedback.breed_id > 0 &&
              ['like', 'dislike'].includes(round.feedback.reaction) &&
              (round.feedback.reason === undefined ||
                [
                  'too_active',
                  'too_large',
                  'too_much_grooming',
                  'too_much_shedding',
                  'too_vocal',
                  'appearance',
                  'other',
                ].includes(round.feedback.reason)))) &&
          (round.conflicts === undefined ||
            (Array.isArray(round.conflicts) &&
              round.conflicts.length <= 7 &&
              round.conflicts.every(
                (conflict) =>
                  conflict &&
                  [
                    'size',
                    'energy',
                    'grooming_needs',
                    'hair_shedding',
                    'barking_level',
                    'learning_ability',
                    'child_friendly',
                  ].includes(conflict.trait) &&
                  typeof conflict.question === 'string' &&
                  typeof conflict.answer === 'string' &&
                  typeof conflict.text === 'string' &&
                  (conflict.original_value === null ||
                    (typeof conflict.original_value === 'number' &&
                      conflict.original_value >= 1 &&
                      conflict.original_value <= 5)) &&
                  typeof conflict.proposed_value === 'number' &&
                  conflict.proposed_value >= 1 &&
                  conflict.proposed_value <= 5 &&
                  ['target', 'maximum', 'minimum'].includes(conflict.original_mode) &&
                  ['target', 'maximum', 'minimum'].includes(conflict.proposed_mode),
              ))) &&
          (round.resolution === undefined ||
            (round.resolution &&
              [
                'size',
                'energy',
                'grooming_needs',
                'hair_shedding',
                'barking_level',
                'learning_ability',
                'child_friendly',
              ].includes(round.resolution.trait) &&
              ['answer', 'text'].includes(round.resolution.choice))),
      ) ||
      !Number.isInteger(draft.submittedAnswerCount) ||
      draft.submittedAnswerCount < 0 ||
      draft.submittedAnswerCount > draft.answers.length
    ) {
      return null;
    }
    if (
      (draft.sessionId || draft.token) &&
      (typeof draft.sessionId !== 'string' ||
        !/^[a-f\d-]{36}$/i.test(draft.sessionId) ||
        typeof draft.token !== 'string' ||
        !/^[a-z\d_-]{32,256}$/i.test(draft.token))
    ) {
      return null;
    }
    const favoriteStage: AdvisorFavoriteStage =
      draft.answers.length || draft.rounds.length
        ? 'done'
        : (draft.favoriteStage ?? (draft.favoriteIds.length ? 'select' : 'ask'));
    return {
      ...draft,
      favoriteStage,
      favoriteInterest:
        draft.favoriteInterest ??
        (favoriteStage === 'ask'
          ? undefined
          : favoriteStage === 'select' || !!draft.favoriteIds.length),
    } as AdvisorDraft;
  } catch {
    return null;
  }
}
